import { Currency } from "@/components/Currency";
import { useModals } from "@/features/modals/hooks";
import { getAllOrdersIds } from "@/features/orders/utils";
import { useActiveProject } from "@/features/project/ActiveProject";
import { type DraftOrder } from "@repo/api";
import {
  Icon,
  ListView,
  Loading,
  chevronRightSmallIcon,
  shoppingBagIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ListViewItemWithActions,
  ListViewItemWithActionsCheckbox,
  LocalizedDateAndTime,
  OneLineInfo,
  SectionContainer,
} from "@repo/ui";
import { hasItems, trackAnalytics } from "@repo/utils";

import { type AddDraftOrderState } from "./AddDraftOrderReducer";

interface SelectOrderStepProps {
  state?: AddDraftOrderState;
  selectDraftOrders: (orderId: string[]) => void;
  customerDraftOrders?: DraftOrder[];
  isLoading: boolean;
}

export const DraftOrderResultList = ({
  state,
  selectDraftOrders,
  customerDraftOrders,
  isLoading,
}: SelectOrderStepProps) => {
  const { opportunity } = useActiveProject();
  const { pushModal } = useModals();
  const orderIds = getAllOrdersIds(opportunity);
  const orders = state?.draftOrders
    ? state?.draftOrders
    : (customerDraftOrders ?? []);

  const onChange = (checked: boolean, item: DraftOrder) => {
    const existing = state?.draftOrderNumbers?.find(
      (orderNumber) => orderNumber === item.draftOrderId
    );
    const newValue = existing
      ? state?.draftOrderNumbers?.filter((f) => f !== item.draftOrderId)
      : [...(state?.draftOrderNumbers ?? []), item.draftOrderId];
    selectDraftOrders(newValue ?? []);
  };

  return (
    <SectionContainer>
      {isLoading && <Loading text={t("prompt.loading")} />}
      {!isLoading && hasItems(orders) && (
        <ListView id="order-result">
          {orders.map((order) => (
            <ListViewItemWithActions
              key={order.draftOrderId}
              icon={shoppingBagIcon}
              title={order.draftOrderId}
              description={
                <div>
                  <OneLineInfo
                    info={[
                      order.status,
                      <Currency
                        key={order.draftOrderId}
                        value={order.metaData?.totalPrice?.priceInclTax ?? 0}
                        currency={order.metaData?.totalPrice?.currencyCode}
                      />,
                    ]}
                  />
                  <OneLineInfo
                    small
                    info={[
                      <LocalizedDateAndTime
                        key="creationDate"
                        date={order.metaData?.orderCreationDate}
                      />,
                      "Draft order",
                      order.buCode,
                    ]}
                  />
                </div>
              }
              action={
                <>
                  <ListViewItemWithActionsCheckbox
                    subtle
                    label={order.draftOrderId}
                    id={`checkbox-${order.draftOrderId}`}
                    value={order.draftOrderId}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      onChange(e.target.checked, order);
                      trackAnalytics("change_order_number");
                    }}
                    checked={
                      orderIds.includes(order.draftOrderId) ||
                      state?.draftOrderNumbers?.includes(order.draftOrderId)
                    }
                    defaultChecked={orderIds?.includes(order.draftOrderId)}
                    disabled={orderIds?.includes(order.draftOrderId)}
                  />
                  <Icon paths={chevronRightSmallIcon} />
                </>
              }
              onClick={() => {
                pushModal({
                  type: "DRAFT_ORDER",
                  draftOrderId: order.draftOrderId,
                });
                trackAnalytics("open_order_modal");
              }}
            />
          ))}
        </ListView>
      )}
    </SectionContainer>
  );
};
